/**
 * Type definitions for the JSX-to-JSON dashboard reconciler.
 *
 * These types define the internal node representation used during reconciliation.
 * The reconciler converts JSX elements into these node types, which are then
 * serialized to DashboardGridData JSON format.
 *
 * @module reconciler/types
 */
import type { dashboardRegistry } from '../registry.ts';
import type { GridItem, GridLayout } from '../schema.ts';

/**
 * Component type discriminator derived from the static registry.
 * This is a union of all registered component type strings.
 */
export type ComponentNodeKind = (typeof dashboardRegistry)[number]['type'];

/**
 * Component node representing a dashboard component (chart, table, kpi, etc.).
 * Props are stored as-is for serialization to JSON.
 */
export interface ComponentNode {
  kind: ComponentNodeKind;
  props: Record<string, unknown>;
}

/**
 * Grid item node representing a positioned container within the grid.
 * Contains exactly one component as its child.
 * Derives shared props from schema's GridItem type for DRY.
 */
export interface GridItemNode extends Pick<GridItem, 'position' | 'caption' | 'captionPosition' | 'layer'> {
  kind: 'grid-item';
  child: ComponentNode | null;
}

/**
 * Grid node representing the root dashboard container.
 * Contains layout configuration and ordered list of grid items.
 * Note: children is typed as Node[] during reconciliation, but should contain only GridItemNodes.
 * The serialize function validates this structure.
 */
export interface GridNode {
  kind: 'grid';
  layout: GridLayout;
  title?: string;
  children: Node[];
}

/**
 * Text node for any text content within the tree.
 * Generally ignored during serialization as dashboards don't render raw text.
 */
export interface TextNode {
  kind: 'text';
  value: string;
}

/**
 * Fragment node for grouping children without a wrapper element.
 * Used internally during reconciliation for unknown element types.
 */
export interface FragmentNode {
  kind: 'fragment';
  children: Node[];
}

/**
 * Union of all node types the reconciler can produce.
 */
export type Node = GridNode | GridItemNode | ComponentNode | TextNode | FragmentNode;

/**
 * Type guard for GridNode.
 */
export function isGridNode(node: Node): node is GridNode {
  return node.kind === 'grid';
}

/**
 * Type guard for GridItemNode.
 */
export function isGridItemNode(node: Node): node is GridItemNode {
  return node.kind === 'grid-item';
}

/**
 * Type guard for ComponentNode.
 */
export function isComponentNode(node: Node): node is ComponentNode {
  return node.kind !== 'grid' && node.kind !== 'grid-item' && node.kind !== 'text' && node.kind !== 'fragment';
}
